import { useAuth } from '@clerk/react'
import { ChevronLeft, Shuffle } from 'lucide-react'
import { useMemo, useState } from 'react'
import { Link, useLocation } from '#router'
import { FeaturebaseBugButton } from '@/components/featurebase-bug-button'
import { ForkStarButton } from '@/components/problem-lists/fork-star-button'
import { MergeButton } from '@/components/problem-lists/merge-button'
import {
  ProblemListProvider,
  useProblemListsOptional,
} from '@/components/problem-lists/problem-list-context'
import { StarDropdown } from '@/components/problem-lists/star-dropdown'
import { DifficultyCell } from '@/components/problems/difficulty-cell'
import { ProblemsFilterPopover } from '@/components/problems/problems-filter-popover'
import { ProblemsSearchBar } from '@/components/problems/problems-search-bar'
import { ProblemsSortPopover } from '@/components/problems/problems-sort-popover'
import { TopicIcon } from '@/components/problems/topic-icon'
import {
  applyFilters,
  type BrowseProblem,
  createProblemsFuse,
  deriveAvailableDifficulties,
  deriveAvailableTopics,
  type SortOption,
  searchProblems,
  sortProblems,
} from '@/components/problems/types'
import { ProgressStatusIcon } from '@/components/ui/progress-status-icon'
import { ProgressSummary } from '@/components/ui/progress-summary'
import { usePageTitle } from '@/hooks/use-document-title'
import { useDevice } from '@/hooks/use-mobile'
import { useProblemsState } from '@/hooks/use-problems-state'
import { isSolvedStatus } from '@/lib/progress-status'
import { trpc } from '@/lib/trpc'
import type { ListRef } from '@/lib/url-state'

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const SORT_OPTIONS = ['difficulty', 'questionId', 'status']

const SORT_POPOVER_OPTIONS = [
  { value: 'difficulty', label: 'Difficulty' },
  { value: 'questionId', label: 'Question ID' },
  { value: 'status', label: 'Status' },
]

// ---------------------------------------------------------------------------
// Public components — one per route
// ---------------------------------------------------------------------------

export function MobileTopicDetailPage() {
  const location = useLocation()
  const topicSlug =
    location.pathname.match(/^\/problems\/topics\/([^/]+)$/)?.[1] ?? ''

  const { data, isLoading } = trpc.problems.browseByTopic.useQuery(
    { topicSlug },
    { enabled: !!topicSlug },
  )

  return (
    <ProblemListProvider>
      <MobileDetailContent
        type="topic"
        slug={topicSlug}
        title={data?.topicName ?? null}
        problems={data?.problems ?? []}
        isLoading={isLoading}
      />
    </ProblemListProvider>
  )
}

export function MobileSubtopicDetailPage() {
  const location = useLocation()
  const subtopicSlug =
    location.pathname.match(/^\/problems\/subtopics\/([^/]+)$/)?.[1] ?? ''

  const { data, isLoading } = trpc.problems.browseBySubtopic.useQuery(
    { subtopicSlug },
    { enabled: !!subtopicSlug },
  )

  return (
    <ProblemListProvider>
      <MobileDetailContent
        type="subtopic"
        slug={subtopicSlug}
        title={data?.subtopicName ?? null}
        problems={data?.problems ?? []}
        parentTopic={
          data?.topicSlug && data?.topicName
            ? { slug: data.topicSlug, name: data.topicName }
            : null
        }
        isLoading={isLoading}
      />
    </ProblemListProvider>
  )
}

export function MobileChapterDetailPage() {
  const location = useLocation()
  const chapterSlug =
    location.pathname.match(/^\/problems\/book\/([^/]+)$/)?.[1] ?? ''

  const { data, isLoading } = trpc.problems.browseByChapter.useQuery(
    { chapterSlug },
    { enabled: !!chapterSlug },
  )

  return (
    <ProblemListProvider>
      <MobileDetailContent
        type="chapter"
        slug={chapterSlug}
        title={data?.chapterTitle ?? null}
        problems={data?.problems ?? []}
        isLoading={isLoading}
      />
    </ProblemListProvider>
  )
}

// ---------------------------------------------------------------------------
// Shared detail content
// ---------------------------------------------------------------------------

function MobileDetailContent({
  type,
  slug,
  title,
  problems,
  isLoading,
  parentTopic = null,
}: {
  type: 'topic' | 'subtopic' | 'chapter'
  slug: string
  title: string | null
  problems: BrowseProblem[]
  isLoading: boolean
  /** Subtopic pages only: a breadcrumb back to the umbrella topic. */
  parentTopic?: { slug: string; name: string } | null
}) {
  const { navigate } = useLocation()
  const { isSignedIn } = useAuth()
  const { isTablet } = useDevice()
  const listCtx = useProblemListsOptional()

  // Use a slug-derived label as fallback so the tab title doesn't flash to the
  // site default during the brief gap between mount and tRPC data arrival.
  const fallback = slug
    ? type === 'chapter'
      ? slug.replace(/^chapter(\d+)$/i, 'Ch.$1')
      : slug
          .split('-')
          .filter(Boolean)
          .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
          .join(' ')
    : null
  usePageTitle(title ?? fallback)

  // Fetch user progress
  const { data: userProgress } = trpc.problems.getUserProgress.useQuery(
    undefined,
    { enabled: !!isSignedIn },
  )

  // Build list refs for the hook
  const userListRefs: ListRef[] = useMemo(
    () =>
      listCtx
        ? listCtx.lists.map((l) => ({
            id: l.id,
            name: l.name,
            isSystem: l.isSystem,
          }))
        : [],
    [listCtx?.lists, listCtx],
  )

  const availableTopics = useMemo(
    () => deriveAvailableTopics(problems),
    [problems],
  )

  const availableDifficulties = useMemo(
    () => deriveAvailableDifficulties(problems),
    [problems],
  )

  const availableLists = useMemo(() => {
    if (!listCtx) return undefined
    return [...listCtx.lists]
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((l) => ({ id: l.id, name: l.name }))
  }, [listCtx?.lists, listCtx])

  // Filter/sort/search state
  const {
    filters,
    sortBy,
    sortDir,
    searchQuery,
    matchMode,
    showTags,
    missingLists,
    setSortBy,
    setSortDir,
    setSearchQuery,
    setMatchMode,
    setShowTags,
    toggleFilterValue,
    resetFilters,
    setFilterOperator,
  } = useProblemsState(
    `bctci-${type}-mobile-${slug}`,
    userListRefs,
    availableTopics,
    SORT_OPTIONS,
  )

  const [dismissed, setDismissed] = useState(false)

  // Search / filter / sort pipeline
  const fuse = useMemo(() => createProblemsFuse(problems), [problems])

  const filteredProblems = useMemo(() => {
    const searched = searchProblems(problems, fuse, searchQuery)
    return applyFilters(
      searched,
      filters,
      matchMode,
      userProgress?.progress,
      listCtx?.memberships,
    )
  }, [
    problems,
    searchQuery,
    fuse,
    filters,
    matchMode,
    userProgress,
    listCtx?.memberships,
  ])

  const sortedProblems = useMemo(
    () =>
      sortProblems(
        filteredProblems,
        sortBy as SortOption,
        sortDir,
        userProgress?.progress,
      ),
    [filteredProblems, sortBy, sortDir, userProgress],
  )

  const solvedCount = useMemo(() => {
    if (!userProgress) return 0
    return problems.filter((p) => isSolvedStatus(userProgress.progress[p.id]))
      .length
  }, [userProgress, problems])

  const progressProblems = useMemo(
    () =>
      problems.map((p) => ({
        id: p.id,
        difficulty: p.difficulty?.name ? { name: p.difficulty.name } : null,
      })),
    [problems],
  )

  // --- Loading state ---
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="text-muted-foreground text-sm">Loading...</div>
      </div>
    )
  }

  // --- Not found state ---
  if (!title) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 px-4 py-16">
        <h2 className="text-lg font-semibold">
          {type.charAt(0).toUpperCase() + type.slice(1)} not found
        </h2>
        <p className="text-muted-foreground text-center text-sm">
          This {type} doesn&apos;t exist or has no problems.
        </p>
        <Link href="/problems" className="text-primary text-sm hover:underline">
          Back to Problems
        </Link>
      </div>
    )
  }

  return (
    <div className="flex flex-col pb-6">
      {/* Back link — to the umbrella topic on a subtopic page, else Problems */}
      <div className="px-4 py-3">
        <Link
          href={
            parentTopic ? `/problems/topics/${parentTopic.slug}` : '/problems'
          }
          className="text-muted-foreground inline-flex items-center gap-1 text-sm hover:underline"
        >
          <ChevronLeft className="h-4 w-4" />
          {parentTopic ? parentTopic.name : 'Problems'}
        </Link>
      </div>

      {/* Header icon */}
      <div className="px-4 pb-2">
        {type !== 'chapter' ? (
          <TopicIcon className="mb-3 h-16 w-20" />
        ) : (
          <a
            href="https://www.amazon.com/dp/195570600X"
            target="_blank"
            rel="noopener noreferrer"
            className="from-muted/60 to-muted/20 mb-3 inline-block rounded-2xl bg-gradient-to-br p-2 shadow-sm ring-1 ring-border/50"
          >
            <img
              src="/images/book-cover.png"
              alt="Beyond Cracking the Coding Interview"
              className="h-32 w-auto rounded-xl shadow-xl"
            />
          </a>
        )}
      </div>

      {/* Title + count */}
      <div className="px-4 pb-2">
        <h1 className="text-xl font-bold">{title}</h1>
        <div className="text-muted-foreground mt-0.5 text-sm">
          {problems.length} question{problems.length !== 1 ? 's' : ''}
        </div>
      </div>

      {/* Action buttons: Fork + Merge */}
      <div className="flex items-center gap-2 px-4 pb-2">
        <ForkStarButton sourceType={type} sourceId={slug} />
        <MergeButton sourceType={type} sourceId={slug} />
      </div>

      {/* Progress chart */}
      <div className="px-4 pb-4">
        <ProgressSummary
          problems={progressProblems}
          progress={userProgress?.progress}
        />
      </div>

      {/* Missing lists banner */}
      {!dismissed && missingLists.length > 0 && (
        <div className="mx-4 mb-3 flex items-center justify-between rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
          <span>Some filters reference lists you don&apos;t have.</span>
          <button
            type="button"
            onClick={() => setDismissed(true)}
            className="ml-3 text-amber-600 hover:text-amber-800 dark:text-amber-400 dark:hover:text-amber-200"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Search / filter / sort toolbar */}
      {isTablet ? (
        <div className="flex items-center gap-1 px-4 pb-3">
          <ProblemsSearchBar value={searchQuery} onChange={setSearchQuery} />
          <ProblemsSortPopover
            sortBy={sortBy}
            sortDir={sortDir}
            onSortChange={(newSortBy, newSortDir) => {
              setSortBy?.(newSortBy)
              setSortDir?.(newSortDir)
            }}
            showTags={showTags}
            onToggleTags={() => setShowTags((v) => !v)}
            sortOptions={SORT_POPOVER_OPTIONS}
          />
          <ProblemsFilterPopover
            filters={filters}
            matchMode={matchMode}
            onMatchModeChange={setMatchMode}
            onToggleFilter={toggleFilterValue}
            onOperatorChange={setFilterOperator}
            availableTopics={availableTopics}
            availableDifficulties={availableDifficulties}
            availableLists={availableLists}
            onReset={resetFilters}
          />
          <div className="flex-1" />
          {isSignedIn && <FeaturebaseBugButton />}
          <span className="text-muted-foreground text-xs">
            {solvedCount}/{problems.length} Solved
          </span>
          <button
            type="button"
            className="text-muted-foreground hover:text-foreground p-2 transition-colors"
            aria-label={`Solve a random problem from this ${type}`}
            onClick={() => {
              if (sortedProblems.length === 0) return
              const slug =
                sortedProblems[
                  Math.floor(Math.random() * sortedProblems.length)
                ].slug
              navigate(`/editor?problem=${slug}`)
            }}
          >
            <Shuffle className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <div className="flex flex-col gap-2 px-4 pb-3">
          <ProblemsSearchBar value={searchQuery} onChange={setSearchQuery} />
          <div className="flex items-center gap-2">
            <ProblemsSortPopover
              sortBy={sortBy}
              sortDir={sortDir}
              onSortChange={(newSortBy, newSortDir) => {
                setSortBy?.(newSortBy)
                setSortDir?.(newSortDir)
              }}
              showTags={showTags}
              onToggleTags={() => setShowTags((v) => !v)}
              sortOptions={SORT_POPOVER_OPTIONS}
            />
            <ProblemsFilterPopover
              filters={filters}
              matchMode={matchMode}
              onMatchModeChange={setMatchMode}
              onToggleFilter={toggleFilterValue}
              onOperatorChange={setFilterOperator}
              availableTopics={availableTopics}
              availableDifficulties={availableDifficulties}
              availableLists={availableLists}
              onReset={resetFilters}
            />
            {isSignedIn && <FeaturebaseBugButton />}
            <span className="text-muted-foreground ml-auto text-xs">
              {solvedCount}/{problems.length} Solved
            </span>
            <button
              type="button"
              className="text-muted-foreground hover:text-foreground p-2 transition-colors"
              aria-label={`Solve a random problem from this ${type}`}
              onClick={() => {
                if (sortedProblems.length === 0) return
                const slug =
                  sortedProblems[
                    Math.floor(Math.random() * sortedProblems.length)
                  ].slug
                navigate(`/editor?problem=${slug}`)
              }}
            >
              <Shuffle className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Problem rows */}
      <div className="flex flex-col">
        {sortedProblems.length > 0 ? (
          sortedProblems.map((problem) => {
            const progress = userProgress?.progress?.[problem.id]
            const via = userProgress?.solvedVia?.[problem.id]
            const rowContent = (
              <>
                {/* Status indicator */}
                <div className="flex w-5 shrink-0 items-center justify-center">
                  <ProgressStatusIcon status={progress} viaTitle={via?.title} />
                </div>

                {/* Problem info */}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    {problem.externalId && (
                      <span className="text-muted-foreground shrink-0 text-xs">
                        {Number(problem.externalId)}.
                      </span>
                    )}
                    <span className="truncate text-sm font-medium">
                      {problem.title}
                    </span>
                  </div>
                  {showTags && problem.topics.length > 0 && (
                    <div className="mt-0.5 flex flex-wrap gap-1">
                      {problem.topics.slice(0, 3).map((topic) => (
                        <span
                          key={topic.slug}
                          className="bg-muted text-muted-foreground rounded px-1.5 py-0.5 text-[10px]"
                        >
                          {topic.name}
                        </span>
                      ))}
                      {problem.topics.length > 3 && (
                        <span className="text-muted-foreground text-[10px]">
                          +{problem.topics.length - 3}
                        </span>
                      )}
                    </div>
                  )}
                </div>

                {/* Difficulty */}
                <DifficultyCell difficulty={problem.difficulty} />
              </>
            )

            // Tablet: use div with programmatic nav so StarDropdown popover
            // can be dismissed by tapping outside without triggering navigation
            if (isTablet) {
              return (
                <div
                  key={problem.id}
                  role="link"
                  tabIndex={0}
                  onClick={() => navigate(`/editor?problem=${problem.slug}`)}
                  className="flex cursor-pointer items-center gap-2 border-b px-3 py-3 transition-colors hover:bg-accent active:bg-accent"
                >
                  {rowContent}
                  <div
                    onClick={(e) => e.stopPropagation()}
                    className="ml-1 mr-1 flex min-h-[44px] min-w-[44px] shrink-0 items-center justify-center"
                  >
                    <StarDropdown problemId={problem.id} />
                  </div>
                </div>
              )
            }

            return (
              <Link
                key={problem.id}
                href={`/editor?problem=${problem.slug}`}
                className="flex items-center gap-2 border-b px-3 py-3 transition-colors hover:bg-accent active:bg-accent"
              >
                {rowContent}
              </Link>
            )
          })
        ) : (
          <div className="text-muted-foreground py-10 text-center text-sm">
            No problems found.
          </div>
        )}
      </div>
    </div>
  )
}
